import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';
import { PageHeader } from '@/components/PageHeader';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <>
      <SEOHead 
        title="Privacy Policy - GrowthLens"
        description="Read GrowthLens's privacy policy to understand how we collect, use, and protect your personal information and assessment data."
        canonical="https://growthlens.in/privacy-policy"
      />
      <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      <PageHeader
        heading="Privacy Policy"
        description="Last updated: September 28, 2025"
      />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        
        <Card className="card-enhanced hover-lift animate-fade-in transition-all duration-300 border-primary/10">
          <CardContent className="space-y-8 text-sm leading-relaxed px-6 md:px-8 py-8">
            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">1. Information We Collect</h2>
              <div className="space-y-3">
                <p>We collect information you provide directly to us, such as when you create an account, take assessments, or contact us for support.</p>
                <p><strong>Personal Information:</strong> Name, email address, profile information, and assessment responses.</p>
                <p><strong>Usage Information:</strong> Information about how you use our service, including assessment completion data and interaction patterns.</p>
                <p><strong>Technical Information:</strong> IP address, browser type, device information, and log data.</p>
              </div>
            </section>

            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">2. How We Use Your Information</h2>
              <ul className="list-disc list-inside space-y-2">
                <li>Provide and maintain our assessment services</li>
                <li>Process your assessment results and generate reports</li>
                <li>Communicate with you about your account and our services</li>
                <li>Improve and personalize your experience</li>
                <li>Ensure security and prevent fraud</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">3. Information Sharing</h2>
              <p>We do not sell, trade, or otherwise transfer your personal information to third parties except as described below:</p>
              <ul className="list-disc list-inside space-y-2 mt-3">
                <li><strong>Service Providers:</strong> We may share information with trusted service providers who assist us in operating our platform</li>
                <li><strong>Legal Requirements:</strong> We may disclose information when required by law or to protect our rights</li>
                <li><strong>Business Transfers:</strong> Information may be transferred in connection with a merger, acquisition, or sale of assets</li>
              </ul>
            </section>

            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">4. Data Security</h2>
              <p>We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no internet transmission is 100% secure.</p>
            </section>

            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">5. Data Retention</h2>
              <p>We retain your information for as long as necessary to provide our services, comply with legal obligations, resolve disputes, and enforce our agreements. Assessment data is typically retained to allow you to access your historical results.</p>
            </section>

            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">6. Your Rights</h2>
              <p>You have the right to:</p>
              <ul className="list-disc list-inside space-y-2 mt-3">
                <li>Access and update your personal information</li>
                <li>Request deletion of your account and data</li>
                <li>Opt-out of marketing communications</li>
                <li>Request a copy of your data</li>
                <li>Lodge a complaint with relevant authorities</li>
              </ul>
            </section>

            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">7. Cookies and Tracking</h2>
              <p>We use cookies and similar technologies to enhance your experience, analyze usage patterns, and provide personalized content. You can control cookie settings through your browser preferences.</p>
            </section>

            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">8. Third-Party Services</h2>
              <p>Our service may contain links to third-party websites or integrate with third-party services. This privacy policy does not apply to those external services, and we encourage you to review their privacy policies.</p>
            </section>

            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">9. Children's Privacy</h2>
              <p>Our service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you become aware that a child has provided us with personal information, please contact us.</p>
            </section>

            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">10. International Transfers</h2>
              <p>Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your information in accordance with applicable data protection laws.</p>
            </section>

            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">11. Updates to This Policy</h2>
              <p>We may update this privacy policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date.</p>
            </section>

            <section className="transition-all duration-200 hover:translate-x-1">
              <h2 className="text-xl font-semibold mb-4 text-primary">12. Contact Us</h2>
              <p>If you have any questions about this privacy policy or our data practices, please contact us through our <Link to="/contact" className="text-primary hover:underline">contact page</Link>.</p>
            </section>
          </CardContent>
        </Card>
      </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;